const OCEANY_RETURN_DEFAULT = "https://app.oceany.ai/";
export function isValidOceanyReturnUrl(url) {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        if (parsed.protocol === "http:" && (host === "localhost" || host === "127.0.0.1")) {
            return true;
        }
        if (parsed.protocol !== "https:")
            return false;
        return host === "oceany.ai" || host.endsWith(".oceany.ai");
    }
    catch {
        return false;
    }
}
/** Session broker cookies are scoped to `.oceany.ai` — unusable from local dev origins. */
export function isLocalhostOrigin() {
    if (typeof window === "undefined")
        return false;
    const host = window.location.hostname.toLowerCase();
    return host === "localhost" || host === "127.0.0.1";
}
export function sanitizeOceanyReturnUrl(url, fallback = OCEANY_RETURN_DEFAULT) {
    if (url && isValidOceanyReturnUrl(url))
        return url;
    return fallback;
}
const LOGOUT_COOKIE = "vida_logout_at";
const RETURN_APP_COOKIE = "vida_return_app";
const HANDOFF_HASH_KEY = "vida_handoff";
const RETURN_APP_COOKIE_MAX_AGE_SECONDS = 300;
const BROADCAST_CHANNEL = "vida-auth";
const SESSION_HEADER = "X-Vida-Session";
const COOKIE_DOMAIN = ".oceany.ai";
function base64UrlEncode(bytes) {
    const bin = String.fromCharCode(...new Uint8Array(bytes));
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomVerifier() {
    const a = new Uint8Array(32);
    crypto.getRandomValues(a);
    return base64UrlEncode(a.buffer);
}
async function sha256Base64Url(plain) {
    const data = new TextEncoder().encode(plain);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return base64UrlEncode(hash);
}
export function decodeJwtPayload(jwt) {
    const parts = jwt.split(".");
    if (parts.length !== 3)
        return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4)
        b64 += "=";
    try {
        return JSON.parse(atob(b64));
    }
    catch {
        return null;
    }
}
function readCookie(name) {
    if (typeof document === "undefined")
        return "";
    const prefix = `${name}=`;
    for (const part of document.cookie.split(";")) {
        const trimmed = part.trim();
        if (trimmed.startsWith(prefix))
            return decodeURIComponent(trimmed.slice(prefix.length));
    }
    return "";
}
function setCrossSubdomainCookie(name, value, maxAgeSeconds) {
    if (typeof document === "undefined")
        return;
    const maxAge = maxAgeSeconds != null ? `; Max-Age=${maxAgeSeconds}` : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax${maxAge}`;
}
function clearCrossSubdomainCookie(name) {
    setCrossSubdomainCookie(name, "", 0);
}
function isSafeReturnPath(path) {
    if (!path || !path.startsWith("/") || path.startsWith("//"))
        return false;
    if (path === "/login" || path.startsWith("/callback"))
        return false;
    return true;
}
function brokerEnabled(cfg) {
    const url = cfg.getSessionBrokerUrl?.()?.trim();
    if (!url)
        return false;
    if (isLocalhostOrigin())
        return false;
    return true;
}
function brokerBase(cfg) {
    return cfg.getSessionBrokerUrl().replace(/\/$/, "");
}
export function createOceanyAuth(cfg) {
    const SK_VERIFIER = `${cfg.storageKeyPrefix}_pkce_verifier`;
    const SK_STATE = `${cfg.storageKeyPrefix}_oauth_state`;
    const SK_TOKENS = `${cfg.storageKeyPrefix}_tokens`;
    const SK_CALLBACK_LOCK = `${cfg.storageKeyPrefix}_callback_lock`;
    const SK_REMEMBER = `${cfg.storageKeyPrefix}_remember`;
    const SK_RETURN_PATH = `${cfg.storageKeyPrefix}_return_path`;
    const SK_RETURN_URL = `${cfg.storageKeyPrefix}_return_url`;
    let syncWatchersInstalled = false;
    function readRememberMeFlag() {
        if (typeof localStorage === "undefined")
            return false;
        return localStorage.getItem(SK_REMEMBER) === "1";
    }
    function writeRememberMeFlag(remember) {
        if (typeof localStorage === "undefined")
            return;
        if (remember)
            localStorage.setItem(SK_REMEMBER, "1");
        else
            localStorage.removeItem(SK_REMEMBER);
    }
    function activeTokenStorage() {
        if (readRememberMeFlag() && typeof localStorage !== "undefined")
            return localStorage;
        if (typeof sessionStorage !== "undefined")
            return sessionStorage;
        return localStorage;
    }
    function alternateTokenStorage(primary) {
        if (typeof localStorage === "undefined" || typeof sessionStorage === "undefined")
            return null;
        return primary === localStorage ? sessionStorage : localStorage;
    }
    function tokenIssuedAt(tokens) {
        if (!tokens)
            return 0;
        if (tokens.issued_at)
            return tokens.issued_at;
        const payload = decodeJwtPayload(tokens.id_token);
        const iat = payload?.iat;
        return typeof iat === "number" ? iat : 0;
    }
    function loadTokens() {
        const primary = activeTokenStorage();
        const raw = primary.getItem(SK_TOKENS);
        if (raw) {
            try {
                return JSON.parse(raw);
            }
            catch {
                primary.removeItem(SK_TOKENS);
            }
        }
        const alt = alternateTokenStorage(primary);
        if (!alt)
            return null;
        const legacy = alt.getItem(SK_TOKENS);
        if (!legacy)
            return null;
        try {
            const parsed = JSON.parse(legacy);
            primary.setItem(SK_TOKENS, legacy);
            alt.removeItem(SK_TOKENS);
            return parsed;
        }
        catch {
            alt.removeItem(SK_TOKENS);
            return null;
        }
    }
    function saveTokens(t) {
        const payload = decodeJwtPayload(t.id_token);
        const iat = payload?.iat;
        const bundle = {
            ...t,
            issued_at: typeof iat === "number" ? iat : t.issued_at,
        };
        if (brokerEnabled(cfg)) {
            delete bundle.refresh_token;
        }
        const storage = activeTokenStorage();
        storage.setItem(SK_TOKENS, JSON.stringify(bundle));
        alternateTokenStorage(storage)?.removeItem(SK_TOKENS);
    }
    function clearTokens() {
        if (typeof localStorage !== "undefined")
            localStorage.removeItem(SK_TOKENS);
        if (typeof sessionStorage !== "undefined")
            sessionStorage.removeItem(SK_TOKENS);
    }
    function saveReturnPath(path) {
        const trimmed = path.trim();
        if (!isSafeReturnPath(trimmed))
            return;
        if (cfg.returnPathPersist) {
            try {
                localStorage.setItem(SK_RETURN_PATH, trimmed);
            }
            catch {
                /* quota */
            }
            try {
                sessionStorage.setItem(SK_RETURN_PATH, trimmed);
            }
            catch {
                /* ignore */
            }
            return;
        }
        sessionStorage.setItem(SK_RETURN_PATH, trimmed);
    }
    function peekReturnPath() {
        if (cfg.returnPathPersist) {
            let path = null;
            try {
                path = localStorage.getItem(SK_RETURN_PATH);
            }
            catch {
                /* ignore */
            }
            if (!path) {
                try {
                    path = sessionStorage.getItem(SK_RETURN_PATH);
                }
                catch {
                    /* ignore */
                }
            }
            return path && isSafeReturnPath(path) ? path : null;
        }
        const path = sessionStorage.getItem(SK_RETURN_PATH);
        return path && isSafeReturnPath(path) ? path : null;
    }
    function consumeReturnPath() {
        const path = peekReturnPath();
        if (cfg.returnPathPersist) {
            try {
                localStorage.removeItem(SK_RETURN_PATH);
                sessionStorage.removeItem(SK_RETURN_PATH);
            }
            catch {
                /* ignore */
            }
            return path ?? "/";
        }
        sessionStorage.removeItem(SK_RETURN_PATH);
        if (!path || path === "/login" || path.startsWith("/callback"))
            return "/";
        return path;
    }
    function captureReturnPathFromSearch(search) {
        const raw = new URLSearchParams(search).get("returnTo");
        if (!raw)
            return;
        try {
            saveReturnPath(decodeURIComponent(raw));
        }
        catch {
            saveReturnPath(raw);
        }
    }
    function saveReturnUrl(url) {
        sessionStorage.setItem(SK_RETURN_URL, sanitizeOceanyReturnUrl(url));
    }
    function peekReturnUrl() {
        const raw = sessionStorage.getItem(SK_RETURN_URL);
        return raw && isValidOceanyReturnUrl(raw) ? raw : null;
    }
    function consumeReturnUrl(fallback = OCEANY_RETURN_DEFAULT) {
        const raw = sessionStorage.getItem(SK_RETURN_URL);
        sessionStorage.removeItem(SK_RETURN_URL);
        return sanitizeOceanyReturnUrl(raw, fallback);
    }
    function captureReturnUrlFromSearch(search) {
        const raw = new URLSearchParams(search).get("return");
        if (raw) {
            try {
                saveReturnUrl(decodeURIComponent(raw));
            }
            catch {
                saveReturnUrl(raw);
            }
            return;
        }
        const fromCookie = readCookie(RETURN_APP_COOKIE);
        if (fromCookie) {
            saveReturnUrl(fromCookie);
            clearCrossSubdomainCookie(RETURN_APP_COOKIE);
        }
    }
    function encodeHandoff(tokens) {
        const payload = JSON.stringify({
            id_token: tokens.id_token,
            access_token: tokens.access_token,
            expires_at: tokens.expires_at,
            issued_at: tokens.issued_at,
        });
        return btoa(payload).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    }
    /** Read a one-time token bundle from the URL hash (login → satellite handoff, Safari-safe). */
    function consumeHandoffFromHash() {
        if (typeof window === "undefined")
            return false;
        const hash = window.location.hash.replace(/^#/, "");
        if (!hash)
            return false;
        const raw = new URLSearchParams(hash).get(HANDOFF_HASH_KEY);
        if (!raw)
            return false;
        try {
            let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
            while (b64.length % 4)
                b64 += "=";
            const bundle = JSON.parse(atob(b64));
            if (!bundle.id_token || typeof bundle.expires_at !== "number")
                return false;
            if (Date.now() >= bundle.expires_at - 60_000)
                return false;
            saveTokens(bundle);
            history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
            return true;
        }
        catch {
            return false;
        }
    }
    /** Append handoff hash when redirecting from login to another *.oceany.ai app. */
    function buildReturnUrlWithHandoff(returnUrl) {
        const safe = sanitizeOceanyReturnUrl(returnUrl);
        if (typeof window === "undefined")
            return safe;
        const tokens = loadTokens();
        if (!tokens?.id_token)
            return safe;
        try {
            const target = new URL(safe);
            if (target.hostname.toLowerCase() === window.location.hostname.toLowerCase())
                return safe;
            target.hash = `${HANDOFF_HASH_KEY}=${encodeURIComponent(encodeHandoff(tokens))}`;
            return target.toString();
        }
        catch {
            return safe;
        }
    }
    function currentPageUrl() {
        if (typeof window === "undefined")
            return OCEANY_RETURN_DEFAULT;
        return `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
    }
    function isOnLoginHost() {
        const loginBase = cfg.getLoginUrl?.()?.trim();
        if (!loginBase || typeof window === "undefined")
            return false;
        try {
            return window.location.hostname === new URL(loginBase).hostname;
        }
        catch {
            return false;
        }
    }
    function redirectToLogin(returnUrl) {
        if (isOnLoginHost())
            return;
        const loginBase = cfg.getLoginUrl?.()?.trim().replace(/\/$/, "");
        if (!loginBase) {
            const path = returnUrl?.startsWith("/") ? returnUrl : window.location.pathname;
            if (isSafeReturnPath(path))
                saveReturnPath(path);
            startLogin();
            return;
        }
        const target = returnUrl ?? currentPageUrl();
        const safe = sanitizeOceanyReturnUrl(target);
        window.location.assign(`${loginBase}/login?return=${encodeURIComponent(safe)}`);
    }
    async function requireSession() {
        installAuthSyncWatchers();
        const ok = await restoreSessionOnStartup();
        if (!ok)
            redirectToLogin();
        return ok;
    }
    function loginPathWithReturn(returnPath) {
        if (!isSafeReturnPath(returnPath))
            return "/login";
        return `/login?returnTo=${encodeURIComponent(returnPath)}`;
    }
    function clearOAuthSession() {
        clearTokens();
        writeRememberMeFlag(false);
        sessionStorage.removeItem(SK_VERIFIER);
        sessionStorage.removeItem(SK_STATE);
        sessionStorage.removeItem(SK_CALLBACK_LOCK);
    }
    function logoutUrl() {
        const params = new URLSearchParams({
            client_id: cfg.getClientId(),
            logout_uri: cfg.getPostLogoutRedirect() || cfg.getRedirectUri(),
        });
        return `${cfg.getCognitoDomain()}/logout?${params.toString()}`;
    }
    function setRememberMe(remember) {
        writeRememberMeFlag(remember);
        const tokens = loadTokens();
        if (tokens)
            saveTokens(tokens);
    }
    function getRememberMe() {
        return readRememberMeFlag();
    }
    function broadcastLogout() {
        try {
            const bc = new BroadcastChannel(BROADCAST_CHANNEL);
            bc.postMessage({ type: "logout" });
            bc.close();
        }
        catch {
            /* unsupported */
        }
    }
    function handleRemoteLogout() {
        clearOAuthSession();
        if (typeof window === "undefined")
            return;
        if (cfg.getLoginUrl?.()?.trim()) {
            redirectToLogin();
            return;
        }
        startLogin();
    }
    function checkLogoutCookie() {
        const logoutAt = Number(readCookie(LOGOUT_COOKIE));
        if (!logoutAt || Number.isNaN(logoutAt))
            return;
        const tokens = loadTokens();
        if (!tokens)
            return;
        if (logoutAt > tokenIssuedAt(tokens)) {
            handleRemoteLogout();
        }
    }
    function installAuthSyncWatchers() {
        if (syncWatchersInstalled || typeof window === "undefined")
            return;
        syncWatchersInstalled = true;
        checkLogoutCookie();
        try {
            const bc = new BroadcastChannel(BROADCAST_CHANNEL);
            bc.onmessage = (ev) => {
                if (ev.data?.type === "logout")
                    handleRemoteLogout();
            };
        }
        catch {
            /* unsupported */
        }
        window.addEventListener("focus", checkLogoutCookie);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible")
                checkLogoutCookie();
        });
    }
    /** Thrown when the broker is unreachable (network/CORS) — distinct from an HTTP error. */
    class BrokerNetworkError extends Error {
    }
    async function brokerFetch(path, init = {}) {
        const headers = new Headers(init.headers);
        headers.set(SESSION_HEADER, "1");
        if (init.accessToken)
            headers.set("Authorization", `Bearer ${init.accessToken}`);
        try {
            return await fetch(`${brokerBase(cfg)}${path}`, {
                ...init,
                headers,
                credentials: "include",
            });
        }
        catch (e) {
            // Safari/WebKit surfaces network + CORS failures as "TypeError: Load failed".
            throw new BrokerNetworkError(`Broker irraggiungibile (${path}): ${e instanceof Error ? e.message : String(e)}`);
        }
    }
    async function revokeRefreshToken(refreshToken) {
        const domain = cfg.getCognitoDomain();
        const clientId = cfg.getClientId();
        if (!domain || !clientId || !refreshToken)
            return;
        try {
            await fetch(`${domain}/oauth2/revoke`, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ token: refreshToken, client_id: clientId }).toString(),
            });
        }
        catch {
            /* best effort */
        }
    }
    async function brokerLogout(accessToken) {
        if (!brokerEnabled(cfg))
            return;
        try {
            await brokerFetch("/session/logout", {
                method: "POST",
                accessToken,
            });
        }
        catch {
            /* best effort */
        }
    }
    function performLogout() {
        const tokens = loadTokens();
        const logoutEpoch = Math.floor(Date.now() / 1000);
        setCrossSubdomainCookie(LOGOUT_COOKIE, String(logoutEpoch));
        setCrossSubdomainCookie(RETURN_APP_COOKIE, currentPageUrl(), RETURN_APP_COOKIE_MAX_AGE_SECONDS);
        broadcastLogout();
        void (async () => {
            if (brokerEnabled(cfg)) {
                await brokerLogout(tokens?.access_token);
            }
            else if (tokens?.refresh_token) {
                await revokeRefreshToken(tokens.refresh_token);
            }
            clearOAuthSession();
            if (!cfg.getClientId() || !cfg.getCognitoDomain()) {
                window.location.assign("/login");
                return;
            }
            window.location.assign(logoutUrl());
        })();
    }
    function startLogin(identityProvider) {
        installAuthSyncWatchers();
        if (!cfg.getClientId() || !cfg.getCognitoDomain() || !cfg.getRedirectUri()) {
            window.alert(cfg.loginAlertMessage ?? "Config OAuth mancante.");
            return;
        }
        const verifier = randomVerifier();
        const state = crypto.randomUUID();
        sessionStorage.setItem(SK_VERIFIER, verifier);
        sessionStorage.setItem(SK_STATE, state);
        void sha256Base64Url(verifier).then((challenge) => {
            const params = new URLSearchParams({
                response_type: "code",
                client_id: cfg.getClientId(),
                redirect_uri: cfg.getRedirectUri(),
                scope: "openid email profile",
                code_challenge: challenge,
                code_challenge_method: "S256",
                state,
            });
            if (identityProvider)
                params.set("identity_provider", identityProvider);
            window.location.assign(`${cfg.getCognitoDomain()}/oauth2/authorize?${params.toString()}`);
        });
    }
    async function exchangeCodeDirect(code, verifier) {
        const body = new URLSearchParams({
            grant_type: "authorization_code",
            client_id: cfg.getClientId(),
            code,
            redirect_uri: cfg.getRedirectUri(),
            code_verifier: verifier,
        });
        const res = await fetch(`${cfg.getCognitoDomain()}/oauth2/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
        });
        const text = await res.text();
        if (!res.ok) {
            let detail = text.slice(0, 300);
            try {
                const j = JSON.parse(text);
                detail = [j.error, j.error_description].filter(Boolean).join(": ") || detail;
            }
            catch {
                /* raw */
            }
            return `Token exchange (${res.status}): ${detail}`;
        }
        const json = JSON.parse(text);
        const idToken = json.id_token;
        const refreshToken = json.refresh_token;
        if (!idToken || !refreshToken)
            return "Risposta token incompleta.";
        saveTokens({
            id_token: idToken,
            access_token: json.access_token ?? "",
            refresh_token: refreshToken,
            expires_at: Date.now() + Number(json.expires_in ?? 3600) * 1000,
        });
        return null;
    }
    async function exchangeCodeViaBroker(code, verifier) {
        let res;
        try {
            res = await brokerFetch("/session/exchange", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    code,
                    code_verifier: verifier,
                    redirect_uri: cfg.getRedirectUri(),
                }),
            });
        }
        catch (e) {
            return e instanceof BrokerNetworkError ? e.message : "Session exchange: errore di rete.";
        }
        const text = await res.text();
        if (!res.ok)
            return `Session exchange (${res.status}): ${text.slice(0, 300)}`;
        const json = JSON.parse(text);
        const idToken = json.id_token;
        if (!idToken)
            return "Risposta sessione incompleta.";
        saveTokens({
            id_token: idToken,
            access_token: json.access_token ?? "",
            expires_at: Date.now() + Number(json.expires_in ?? 3600) * 1000,
        });
        return null;
    }
    async function handleOAuthCallback() {
        installAuthSyncWatchers();
        const q = new URLSearchParams(window.location.search);
        const code = q.get("code");
        const state = q.get("state");
        const err = q.get("error");
        if (err) {
            history.replaceState({}, "", window.location.pathname);
            const errDesc = q.get("error_description");
            return errDesc ? `${err}: ${decodeURIComponent(errDesc)}` : err;
        }
        if (!code || !state)
            return null;
        if (sessionStorage.getItem(SK_CALLBACK_LOCK) === code) {
            return loadTokens() ? null : "Accesso in corso…";
        }
        sessionStorage.setItem(SK_CALLBACK_LOCK, code);
        const expectedState = sessionStorage.getItem(SK_STATE);
        const verifier = sessionStorage.getItem(SK_VERIFIER);
        if (!expectedState || state !== expectedState) {
            sessionStorage.removeItem(SK_CALLBACK_LOCK);
            return "state OAuth non valido o sessione scaduta.";
        }
        if (!verifier) {
            sessionStorage.removeItem(SK_CALLBACK_LOCK);
            return "PKCE verifier mancante.";
        }
        try {
            const exchangeErr = brokerEnabled(cfg)
                ? await exchangeCodeViaBroker(code, verifier)
                : await exchangeCodeDirect(code, verifier);
            if (exchangeErr)
                return exchangeErr;
            sessionStorage.removeItem(SK_VERIFIER);
            sessionStorage.removeItem(SK_STATE);
            return null;
        }
        finally {
            sessionStorage.removeItem(SK_CALLBACK_LOCK);
        }
    }
    async function refreshTokensDirect() {
        const t = loadTokens();
        if (!t?.refresh_token)
            return "Non autenticato.";
        const body = new URLSearchParams({
            grant_type: "refresh_token",
            client_id: cfg.getClientId(),
            refresh_token: t.refresh_token,
        });
        const res = await fetch(`${cfg.getCognitoDomain()}/oauth2/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
        });
        if (!res.ok) {
            clearTokens();
            return "Refresh fallito.";
        }
        const json = (await res.json());
        const idToken = json.id_token;
        if (!idToken) {
            clearTokens();
            return "Refresh: id_token mancante.";
        }
        saveTokens({
            id_token: idToken,
            access_token: json.access_token || t.access_token,
            refresh_token: json.refresh_token || t.refresh_token,
            expires_at: Date.now() + Number(json.expires_in ?? 3600) * 1000,
        });
        return null;
    }
    async function refreshTokensViaBroker() {
        const t = loadTokens();
        let res;
        try {
            res = await brokerFetch("/session/refresh", { method: "POST" });
        }
        catch (e) {
            // Network/CORS failure: transient — keep any local token, do NOT force logout.
            return e instanceof BrokerNetworkError ? e.message : "Refresh: errore di rete.";
        }
        if (!res.ok) {
            clearTokens();
            return res.status === 401 ? "Non autenticato." : "Refresh fallito.";
        }
        const json = (await res.json());
        const idToken = json.id_token;
        if (!idToken) {
            clearTokens();
            return "Refresh: id_token mancante.";
        }
        saveTokens({
            id_token: idToken,
            access_token: json.access_token || t?.access_token || "",
            expires_at: Date.now() + Number(json.expires_in ?? 3600) * 1000,
        });
        return null;
    }
    async function refreshTokensWithStoredRefresh() {
        return brokerEnabled(cfg) ? refreshTokensViaBroker() : refreshTokensDirect();
    }
    async function bootstrapViaBroker() {
        const maxAttempts = 4;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const err = await refreshTokensViaBroker();
            if (err == null)
                return true;
            // Safari may not attach the HttpOnly refresh cookie on the first cross-subdomain
            // fetch immediately after login redirects back to the satellite app.
            const retryable = err.startsWith("Broker irraggiungibile") || err === "Non autenticato." || err === "Refresh fallito.";
            if (!retryable || attempt + 1 >= maxAttempts)
                return false;
            await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
        }
        return false;
    }
    async function restoreSessionOnStartup() {
        installAuthSyncWatchers();
        if (consumeHandoffFromHash())
            return true;
        checkLogoutCookie();
        if (brokerEnabled(cfg)) {
            // Cookie HttpOnly is source of truth — never trust local id/access without broker refresh.
            return bootstrapViaBroker();
        }
        const tokens = loadTokens();
        if (!tokens)
            return false;
        if (Date.now() < tokens.expires_at - 60_000)
            return true;
        if (!tokens.refresh_token?.trim())
            return false;
        return (await refreshTokensWithStoredRefresh()) == null;
    }
    async function refreshIfNeeded() {
        const t = loadTokens();
        if (!t) {
            if (brokerEnabled(cfg))
                return (await refreshTokensViaBroker()) == null ? null : "Non autenticato.";
            return "Non autenticato.";
        }
        if (Date.now() < t.expires_at - 60_000)
            return null;
        return refreshTokensWithStoredRefresh();
    }
    async function getIdToken(forceRefresh = false) {
        const err = forceRefresh
            ? await refreshTokensWithStoredRefresh()
            : await refreshIfNeeded();
        if (err)
            throw new Error(err);
        const t = loadTokens();
        if (!t?.id_token)
            throw new Error("Non autenticato.");
        return t.id_token;
    }
    async function forceRefreshTokens() {
        return refreshTokensWithStoredRefresh();
    }
    function currentUserSub() {
        const t = loadTokens();
        if (!t)
            return "anonymous";
        const payload = decodeJwtPayload(t.id_token);
        return payload?.sub ?? "anonymous";
    }
    function currentUserEmail() {
        const t = loadTokens();
        if (!t)
            return "";
        const payload = decodeJwtPayload(t.id_token);
        return (payload?.email ??
            payload?.preferred_username ??
            "");
    }
    function currentUserDisplayName() {
        const t = loadTokens();
        if (!t)
            return "anonymous";
        const payload = decodeJwtPayload(t.id_token);
        const name = payload?.name ??
            payload?.given_name ??
            payload?.email ??
            payload?.preferred_username ??
            "";
        return name.trim() || currentUserSub();
    }
    return {
        SK_RETURN_PATH,
        loadTokens,
        saveTokens,
        clearTokens,
        setRememberMe,
        getRememberMe,
        saveReturnPath,
        peekReturnPath,
        consumeReturnPath,
        captureReturnPathFromSearch,
        saveReturnUrl,
        peekReturnUrl,
        consumeReturnUrl,
        captureReturnUrlFromSearch,
        buildReturnUrlWithHandoff,
        consumeHandoffFromHash,
        redirectToLogin,
        requireSession,
        loginPathWithReturn,
        clearOAuthSession,
        performLogout,
        startLogin,
        handleOAuthCallback,
        restoreSessionOnStartup,
        refreshIfNeeded,
        getIdToken,
        forceRefreshTokens,
        logoutUrl,
        decodeJwtPayload,
        currentUserSub,
        currentUserEmail,
        currentUserDisplayName,
        installAuthSyncWatchers,
    };
}
